import { Box, BoxProps, useMediaQuery } from "@mui/material";
import { Children, ReactNode } from "react";

const CARD_MIN_WIDTH = 424;
const CARD_GAP = 24;

export type RequestStepListProps = {
    children: ReactNode;
    sx?: BoxProps["sx"];
    availableWidth?: number;
};

/**
 * Layout wrapper for request step cards.
 * - max 3 cards per row
 * - cards align left
 * - cards wrap automatically
 * - consumer can center this wrapper (e.g. via mx: "auto")
 */
export const RequestStepList = ({ children, sx, availableWidth }: RequestStepListProps) => {
    const cardCount = Children.count(children);
    const maxColumnsByCount = Math.min(Math.max(cardCount, 1), 3);
    const isOneColumn = useMediaQuery(`(max-width:${CARD_MIN_WIDTH * 2 + CARD_GAP - 1}px)`);
    const isTwoColumns = useMediaQuery(`(max-width:${CARD_MIN_WIDTH * 3 + CARD_GAP * 2 - 1}px)`);
    const maxColumnsByViewport = isOneColumn ? 1 : isTwoColumns ? 2 : 3;
    const maxColumnsByAvailableWidth = typeof availableWidth === "number"
        ? Math.max(1, Math.min(3, Math.floor((availableWidth + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP))))
        : undefined;
    const columns = Math.min(maxColumnsByCount, maxColumnsByAvailableWidth ?? maxColumnsByViewport);
    const dynamicMaxWidth = columns === 1 ? "100%" : CARD_MIN_WIDTH * columns + CARD_GAP * (columns - 1);
    const gridTemplateColumns = columns === 1 ? "minmax(0, 1fr)" : `repeat(${columns}, ${CARD_MIN_WIDTH}px)`;

    return (
        <Box
            sx={[
                {
                    display: "grid",
                    gridTemplateColumns,
                    gap: `${CARD_GAP}px`,
                    justifyContent: "flex-start",
                    width: "100%",
                    maxWidth: dynamicMaxWidth,
                },
                sx,
            ]}
        >
            {children}
        </Box>
    );
};
